import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import { ExclamationCircleIcon, TrashIcon } from "@heroicons/react/24/solid";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import { translateText } from "../../utils/translation";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import { useSearchParams } from "react-router-dom";
import Dropdown from "../forms/Dropdown";
import Filters from "../layout/Filters";
import Card from "../layout/Card";
import DangerZone from "../forms/DangerZone";
import {
  deleteTaskLogs,
  getTaskLogs,
  getTasks,
  updateTaskLog,
} from "../../slices/tasks";
import { getRooms } from "../../slices/rooms";
import Checkbox from "../forms/Checkbox";
import Table from "../layout/Table";
import Badge from "../layout/Badge";
import { getColour } from "../../utils/colours";
import { computeMean } from "../../utils/arrays";
import Grid from "../layout/Grid";
import WaffleChart from "../charts/block/WaffleChart";
import DonutChart from "../charts/radial/DonutChart";

export default function Logs() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams] = useSearchParams();
  const pageNum =
    searchParams.get("page") === null
      ? 1
      : parseInt(searchParams.get("page") as string);
  const searchCategory = searchParams.get("category");
  const searchTask = searchParams.get("task");
  const dateRangeTerm = searchParams.get("dateRange");

  // Fetch data from server
  useEffect(() => {
    dispatch(
      getTasks({
        category: searchCategory,
        task: searchTask,
        dateRange: dateRangeTerm,
      }),
    );
    dispatch(
      getTaskLogs({
        dateRange: dateRangeTerm,
        task: searchTask,
        pageNum,
        category: searchCategory,
      }),
    );
    dispatch(getRooms());
  }, [dateRangeTerm, dispatch, searchCategory, searchTask, pageNum]);

  // Set state
  const [category, setCategory] = useState({ value: "" });
  const [task, setTask] = useState({ value: "" });
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [selectedLogs, setSelectedLogs] = useState<string[]>([]);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [selectedLog, setSelectedLog] = useState<{
    id: string;
    datetime: string;
    task: string;
    room: string;
    status: string;
  }>({
    id: "",
    datetime: "",
    task: "",
    room: "",
    status: "C",
  });

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { tasks, logs, options } = useAppSelector((state) => state.tasks);
  const { rooms } = useAppSelector((state) => state.rooms);
  const language = user === null ? "EN" : user.language;

  // Functions
  function selectLog(logId: string) {
    const updatedSelectedLogs = [...selectedLogs];
    if (!updatedSelectedLogs.includes(logId)) {
      updatedSelectedLogs.push(logId);
    } else {
      updatedSelectedLogs.splice(updatedSelectedLogs.indexOf(logId), 1);
    }
    setSelectedLogs(updatedSelectedLogs);
  }

  function getSelectedLogsText(numLogs: number) {
    if (language === "EN") {
      if (numLogs === 1) return `${numLogs} log selected`;
      return `${numLogs} logs selected`;
    }
    if (language === "FR") {
      if (numLogs === 1) return `${numLogs} journal sélectionné`;
      return `${numLogs} journaux sélectionnés`;
    }
    if (language === "IT") {
      if (numLogs === 1) return `${numLogs} log selezionato`;
      return `${numLogs} log selezionati`;
    }
    if (language === "DE") {
      if (numLogs === 1) return `${numLogs} Protokoll ausgewählt`;
      return `${numLogs} Protokolle ausgewählt`;
    }
    return `${numLogs} logs selected`;
  }

  function selectAllLogs(logId: any) {
    const currentLogIds = logsData.map((log) => log.id);

    // If all are selected, unselect all
    if (
      selectedLogs.length > 0 &&
      selectedLogs.length === currentLogIds.length
    ) {
      setSelectedLogs([]);
      return;
    }

    // Otherwise, select all
    const logIds = logsData.map((log) => log.id);
    setSelectedLogs(logIds);
  }

  function buildChartData() {
    // Compute completion rate
    let completionRates = tasksData.map((task) => {
      if (task.completion_status.completed) return 100;
      const totalRooms = task.completion_status.task_rooms.length;
      const remainingRooms = task.completion_status.outstanding_rooms.length;
      const completedRooms = totalRooms - remainingRooms;
      return (completedRooms / totalRooms) * 100;
    });
    const completionRate = computeMean(completionRates);

    // Compute number of completed vs incomplete tasks
    const completedTasks = tasksData.filter(
      (task) => task.completion_status.completed,
    ).length;
    const incompleteTasks = tasksData.filter(
      (task) => !task.completion_status.completed,
    ).length;

    // Count number of outstanding tasks in rooms
    let outstandingRooms = 0;
    let totalTasks = 0;
    tasksData.forEach((task) => {
      outstandingRooms += task.completion_status.outstanding_rooms.length;
      totalTasks += task.completion_status.task_rooms.length;
    });

    return {
      completion_rate: completionRate,
      task_completion: {
        completed: completedTasks,
        incomplete: incompleteTasks,
      },
      outstanding_rooms: {
        outstanding: outstandingRooms,
        total: totalTasks,
      },
    };
  }

  function getOutstandingRoomTextColour() {
    const pctOutstanding =
      (chartData.outstanding_rooms.outstanding /
        chartData.outstanding_rooms.total) *
      100;
    if (pctOutstanding < 33) return "success";
    if (pctOutstanding < 66) return "warning";
    return "danger";
  }

  function getDeleteButtonText(selectedLogs: string[]) {
    if (language === "EN") {
      if (selectedLogs.length === 1) return "1 log";
      return `${selectedLogs.length} logs`;
    }
    if (language === "FR") {
      if (selectedLogs.length === 1) return "1 journal";
      return `${selectedLogs.length} journaux`;
    }
    if (language === "IT") {
      if (selectedLogs.length === 1) return "1 log";
      return `${selectedLogs.length} log`;
    }
    if (language === "DE") {
      if (selectedLogs.length === 1) return "1 Protokoll";
      return `${selectedLogs.length} Protokolle`;
    }
    return `${selectedLogs.length} logs`;
  }

  function selectLogToEdit(e: any, args: any[]) {
    e.preventDefault();
    const [taskLogId] = args;
    const logData = logsData.filter((log) => log.id === taskLogId)[0];
    setSelectedLog({
      id: logData.id,
      datetime: logData.datetime,
      task: logData.task[0],
      room: logData.room[0],
      status: logData.status[0],
    });
    setShowEditModal(true);
  }

  async function confirmEditTaskLog(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const { id, task, room, status } = selectedLog;

    // Validate text fields
    if (task === "") {
      dispatch(
        addAlert(generateAlertText("missing_task", language), "danger", 5000),
      );
      return;
    }
    if (room === "") {
      dispatch(
        addAlert(generateAlertText("missing_room", language), "danger", 5000),
      );
      return;
    }
    if (status === "") {
      dispatch(
        addAlert(generateAlertText("missing_status", language), "danger", 5000),
      );
      return;
    }

    setShowEditModal(false);
    const res: any = await dispatch(
      updateTaskLog({
        taskLogId: id,
        property: user.property.id,
        task,
        profile: user.id,
        room,
        status,
      }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("update_task_log", language),
          "success",
          3000,
        ),
      );
      setSelectedLog({
        id: "",
        datetime: "",
        task: "",
        room: "",
        status: "C",
      });
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  async function confirmDeleteLog(e: any, args: any[]) {
    e.preventDefault();
    const [taskLogIds] = args;
    if (taskLogIds.length === 0) return;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 3000),
      );
      return;
    }
    if (taskLogIds === null || taskLogIds === undefined) {
      dispatch(
        addAlert(
          generateAlertText("missing_task_logs", language),
          "danger",
          3000,
        ),
      );
      return;
    }
    setShowEditModal(false);

    const res: any = await dispatch(
      deleteTaskLogs({ taskLogIds: [taskLogIds] }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("delete_success", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("delete_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  function checkDeletionModal(e: any, args: any[]) {
    e.preventDefault();
    const [taskLogIds] = args;
    if (taskLogIds.length === 0) return;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 3000),
      );
      return;
    }
    if (taskLogIds === null || taskLogIds === undefined) {
      dispatch(
        addAlert(
          generateAlertText("missing_task_logs", language),
          "danger",
          3000,
        ),
      );
      return;
    }
    setShowDeleteModal(true);
  }

  async function confirmDeleteLogs(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }

    if (selectedLogs.length === 0) return;
    setShowDeleteModal(false);

    const res: any = await dispatch(
      deleteTaskLogs({ taskLogIds: selectedLogs }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("delete_success", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("delete_error", language), "danger", 3000),
      );
      setShowDeleteModal(true);
    }
  }

  // Declare constants to be used
  const tasksData = tasks.data === null ? [] : tasks.data;
  const logsData = logs.data === null ? [] : logs.data;
  const roomsData = rooms.data === null ? [] : rooms.data;
  const tasksCategories =
    options.category === undefined
      ? []
      : Object.keys(options.category).map((key) => ({
          value: key,
          label: options.category[key][language],
        }));
  const tasksRoomChoices = roomsData.map((obj) => [obj.id, obj.name]);
  const tasksStatusChoices =
    options.status === undefined
      ? []
      : Object.keys(options.status).map((key) => [
          key,
          options.status[key][language],
        ]);
  const tasksFilterValues =
    tasksData.length === 0
      ? []
      : tasksData.map((obj) => ({
          value: obj.id,
          label: obj.name[language],
        }));
  const tasksChoices =
    tasksData.length === 0
      ? []
      : tasksData.map((obj) => [obj.id, obj.name[language]]);
  const chartData = buildChartData();
  const completionRateData = [
    {
      id: "completion_rate",
      label: "Completion Rate",
      value: chartData.completion_rate,
    },
  ];
  const completeVsIncomplete = [
    {
      id: "Complete",
      label: "Complete",
      value: chartData.task_completion.completed,
    },
    {
      id: "Incomplete",
      label: "Incomplete",
      value: chartData.task_completion.incomplete,
    },
  ];

  return (
    <Template>
      {/* Header */}
      <Header text={translateText("task_logs_title", language)} />

      {/* Edit log modal */}
      <Modal
        show={showEditModal}
        setShow={setShowEditModal}
        confirmText={translateText("edit_task_log", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmEditTaskLog}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("edit_task_log", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Task */}
              <Dropdown
                id="task"
                className="col-span-full"
                label={translateText("task", language)}
                showLabel={true}
                required={true}
                state={selectedLog}
                stateName="task"
                onChange={setSelectedLog}
                defaultValue={selectedLog.task}
              >
                {tasksChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Room */}
              <Dropdown
                id="room"
                className="col-span-full"
                label={translateText("room", language)}
                showLabel={true}
                required={true}
                state={selectedLog}
                stateName="room"
                onChange={setSelectedLog}
                defaultValue={selectedLog.room}
              >
                {tasksRoomChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Status */}
              <Dropdown
                id="status"
                className="col-span-full"
                label={translateText("status", language)}
                showLabel={true}
                required={true}
                state={selectedLog}
                stateName="status"
                onChange={setSelectedLog}
                defaultValue={selectedLog.status}
              >
                {tasksStatusChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Danger zone */}
              <DangerZone
                objectId={selectedLog.id}
                handler={confirmDeleteLog}
                language={language}
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* Delete log modal */}
      <Modal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        confirmText={translateText("delete", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmDeleteLogs}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("delete_logs", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              <p className="text-sm text-slate-600">
                {translateText("delete_logs_confirmation", language)}
              </p>
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {tasks.loading || logs.loading || rooms.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <Filters
              language={language}
              className="pb-10"
              filters={[
                {
                  id: "category",
                  name: "Category",
                  options: tasksCategories,
                },
                {
                  id: "task",
                  name: "Task",
                  options: tasksFilterValues,
                },
              ]}
              queryParams={[
                ["category", searchCategory],
                ["task", searchTask],
                ["dateRange", dateRangeTerm],
              ]}
              handlers={{
                category: {
                  state: category,
                  stateName: "value",
                  onChange: setCategory,
                },
                task: {
                  state: task,
                  stateName: "value",
                  onChange: setTask,
                },
                dateRange: {
                  state: dateRange,
                  stateName: "N/A",
                  onChange: setDateRange,
                },
              }}
              loading={tasks.loading || logs.loading}
              baseUrl="/tasks/logs"
              showDateRange={true}
              showFilters={true}
              showSearchBar={false}
            />

            {/* Charts */}
            <Grid
              defaultColumns={1}
              columnsLargeViewport={3}
              columnsMediumViewport={3}
              columnsSmallViewport={3}
              className="mb-10"
            >
              {/* Completion rate */}
              <Card className="overflow-x-auto scrollbar-invisible">
                <div className="flex w-full items-center justify-between space-x-6">
                  <div className="flex-1">
                    <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                      <h3 className="text-base font-semibold leading-6 text-gray-900">
                        {translateText("completion_rate_chart_title", language)}
                      </h3>
                      <span className="text-xs text-gray-400">
                        {translateText(
                          "completion_rate_chart_subtitle",
                          language,
                        )}
                      </span>
                    </div>
                    <WaffleChart
                      data={completionRateData}
                      className="mx-auto text-sm lg:h-80 md:h-64 sm:h-56 h-40"
                      reverse={true}
                    />
                  </div>
                </div>
              </Card>

              {/* Number of outstanding tasks */}
              <Card className="overflow-x-auto scrollbar-invisible">
                <div className="flex w-full items-center justify-between space-x-6">
                  <div className="flex-1">
                    <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                      <h3 className="text-base font-semibold leading-6 text-gray-900">
                        {translateText(
                          "outstanding_tasks_chart_title",
                          language,
                        )}
                      </h3>
                      <span className="text-xs text-gray-400">
                        {translateText(
                          "outstanding_tasks_chart_subtitle",
                          language,
                        )}
                      </span>
                    </div>
                    <div className="flex justify-center lg:h-80 md:h-64 sm:h-56 h-40 items-center">
                      <h3
                        className={`text-9xl font-bold text-${getOutstandingRoomTextColour()}`}
                      >
                        {chartData.outstanding_rooms.outstanding}
                      </h3>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Number completed tasks vs incomplete tasks */}
              <Card className="overflow-x-auto scrollbar-invisible">
                <div className="flex w-full items-center justify-between space-x-6">
                  <div className="flex-1">
                    <div className="border-b border-gray-200 px-4 pb-4 pt-0 mb-4 sm:px-6">
                      <h3 className="text-base font-semibold leading-6 text-gray-900">
                        {translateText(
                          "complete_vs_incomplete_chart_title",
                          language,
                        )}
                      </h3>
                      <span className="text-xs text-gray-400">
                        {translateText(
                          "complete_vs_incomplete_chart_subtitle",
                          language,
                        )}
                      </span>
                    </div>
                    <DonutChart
                      data={completeVsIncomplete}
                      className="mx-auto text-sm lg:h-80 h-64"
                    />
                  </div>
                </div>
              </Card>
            </Grid>

            {/* Table of logs */}
            {logsData.length > 0 ? (
              <div className="mx-1 mb-10">
                {/* Table actions */}
                {selectedLogs.length > 0 && (
                  <div>
                    <p className="text-sm text-granite italic">
                      {getSelectedLogsText(selectedLogs.length)}
                    </p>
                    <div
                      className={`mt-2 mb-4 grid grid-cols-1 md:grid-cols-${
                        selectedLogs.length === 1 ? "2" : "1"
                      } space-y-2 md:space-y-0 gap-x-2`}
                    >
                      {/* Edit log */}
                      {selectedLogs.length === 1 && (
                        <Button
                          type="button"
                          className={`${getButtonStyle("solid", "warning")}`}
                          onClick={selectLogToEdit}
                          onClickArgs={[selectedLogs[0]]}
                        >
                          <PencilSquareIcon
                            className="w-5 h-5 mr-2"
                            aria-hidden="true"
                          />
                          {translateText("edit", language)}
                        </Button>
                      )}

                      {/* Delete logs */}
                      <Button
                        type="button"
                        className={`${getButtonStyle("solid", "danger")}`}
                        onClick={checkDeletionModal}
                        onClickArgs={[selectedLogs]}
                      >
                        <TrashIcon
                          className="w-5 h-5 mr-2"
                          aria-hidden="true"
                        />
                        {`${translateText(
                          "delete",
                          language,
                        )} ${getDeleteButtonText(selectedLogs)}`}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Table */}
                <Table
                  className={null}
                  header={
                    <tr>
                      <th className="px-2 py-3">
                        <Checkbox
                          id="select-all"
                          onChange={selectAllLogs}
                          checked={
                            selectedLogs.length > 0 &&
                            selectedLogs.length === logsData.length
                          }
                        />
                      </th>
                      <th className="px-2 py-3">
                        {translateText("timestamp", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("task", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("category", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("user", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("room", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("status", language)}
                      </th>
                    </tr>
                  }
                  body={logsData.map((log) => (
                    <tr
                      key={log.id}
                      className={`${
                        selectedLogs.includes(log.id) && "bg-light/20"
                      } hover:bg-light/20`}
                      onClick={() => selectLog(log.id)}
                    >
                      <td className="border-r border-light/50 px-2 py-3">
                        <Checkbox
                          id={log.id}
                          onChange={selectLog}
                          checked={selectedLogs.includes(log.id)}
                        />
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {log.datetime}
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {log.task[1]}
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        <Badge
                          text={log.task_category[1]}
                          bgColour={getColour(log.task_category[0])}
                          textColour={`${getColour(log.task_category[0])}-dark`}
                          ringColour={`${getColour(log.task_category[0])}-dark`}
                        />
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {log.profile}
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {log.room[1]}
                      </td>
                      <td className="px-2 py-3">
                        <Badge
                          text={log.status[1]}
                          bgColour={getColour(log.status[0])}
                          textColour={`${getColour(log.status[0])}-dark`}
                          ringColour={`${getColour(log.status[0])}-dark`}
                        />
                      </td>
                    </tr>
                  ))}
                  showPagination={true}
                  paginationHasPrevious={logs.pagination.has_previous}
                  paginationHasNext={logs.pagination.has_next}
                  paginationCurrentPage={logs.pagination.current_page}
                  paginationTotalPages={logs.pagination.total_pages}
                  paginationTotalObjects={logs.pagination.total_objects}
                  paginationQueryParams={["category", "task", "dateRange"]}
                />
              </div>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("not_enough_data", language)}
                subtext={translateText("not_enough_data_log_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
